import { forwardRef, useRef, type ComponentPropsWithoutRef, type ReactNode } from 'react'
import * as RxAccordion from '@radix-ui/react-accordion'
import { cn } from './cn'
import { ChevronDown } from 'lucide-react'
import { useAutoFocusFirstField } from '@/shared/hooks/use-auto-focus-first-field'

/**
 * Accordion — Radix primitive styled with Night Ops tokens.
 *
 * Default pattern for the booking panel: each section renders a summary
 * header (label + current value preview) that expands to the editable
 * body. Follows the "summary card" pattern in ui-ux-patterns.md §3.5.
 */
export const Accordion = RxAccordion.Root

export type AccordionItemProps = ComponentPropsWithoutRef<typeof RxAccordion.Item>

export const AccordionItem = forwardRef<HTMLDivElement, AccordionItemProps>(
  ({ className, ...rest }, ref) => (
    <RxAccordion.Item
      ref={ref}
      className={cn('accordion-item', className)}
      {...rest}
    />
  ),
)
AccordionItem.displayName = 'AccordionItem'

/**
 * Trigger row — renders as a flex row: [icon?] [label] [summary] [chevron].
 * `summary` is the inline preview of the section's current value; it's
 * visible in both collapsed and expanded states but faded when expanded.
 */
export function AccordionTrigger({
  icon,
  label,
  summary,
  className,
}: {
  icon?: ReactNode
  label: ReactNode
  summary?: ReactNode
  className?: string
}) {
  return (
    <RxAccordion.Header asChild>
      <div className={cn('accordion-trigger-wrap', className)}>
        <RxAccordion.Trigger className="accordion-trigger">
          {icon && <span className="accordion-icon">{icon}</span>}
          <span className="accordion-label">{label}</span>
          {summary != null && (
            <span className="accordion-summary">{summary}</span>
          )}
          <span className="accordion-chevron" aria-hidden="true">
            <ChevronDown size={14} />
          </span>
        </RxAccordion.Trigger>
      </div>
    </RxAccordion.Header>
  )
}

export const AccordionContent = forwardRef<
  HTMLDivElement,
  ComponentPropsWithoutRef<typeof RxAccordion.Content>
>(({ className, children, ...rest }, ref) => {
  // Radix AccordionContent mounts ONLY when the section is expanded
  // (default, no `forceMount`), so mount-time IS expand-time and the
  // hook fires on every open. No `skipInitialMount` needed.
  const innerRef = useRef<HTMLDivElement>(null)
  useAutoFocusFirstField(innerRef, true)
  return (
    <RxAccordion.Content
      ref={ref}
      className={cn('accordion-content', className)}
      {...rest}
    >
      <div ref={innerRef} className="accordion-content-inner">
        {children}
      </div>
    </RxAccordion.Content>
  )
})
AccordionContent.displayName = 'AccordionContent'
